import React, { useCallback, useEffect, useState } from 'react';
import FAQItem from './faq-item';
import KnowledgeBaseResponse from './knowledge-base-response';
import KnowledgeBaseSearch from './knowledge-base-search';
import { FaqApiResponse, RagApiResponse, fetchFaqDataHistory, fetchFaqRagData, updateFeedback } from './service';
import { useMsal } from '@azure/msal-react';

interface InsightsProps {
	data: any;
}

type StepNameToIdMap = {
	[stepName: string]: number;
};

const Insights: React.FC<InsightsProps> = ({ data }) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [faqDataHistory, setFaqDataHistory] = useState<FaqApiResponse>({ q: [], a: [] });
	const [ragData, setRagData] = useState<RagApiResponse | null>(null);
	const [visibleAnswers, setVisibleAnswers] = useState<boolean[]>([]);
	const [stepId, setStepId] = useState<number | undefined>(2);
	const [inputQuery, setInputQuery] = useState<string>('');
	const [likes, setLikes] = useState<number>(0);
	const [dislikes, setDislikes] = useState<number>(0);
	const { instance } = useMsal();
	const accounts = instance.getAllAccounts();
	const loggedInUserName = accounts && accounts.length > 0 ? accounts[0].name : '';

	useEffect(() => {
		if (ragData) {
			setLikes(ragData.likes || 0);
			setDislikes(ragData.dislikes || 0);
		}
	}, [ragData]);

	useEffect(() => {
		const fetchStepId = async () => {
			const stepNameToIdMap: StepNameToIdMap = {
				'Address Search': 2,
				'Customer Info': 3,
				'Internet Plans': 4,
				'Phone Plans': 5,
				'Phone Configuration': 6,
				'Schedule Install': 7,
				'Billing Information': 8,
				'Review and Verification': 9,
			};
			const stepId = stepNameToIdMap[data.step_name];
			if (stepId !== undefined) {
				setStepId(stepId);
			}
		};
		fetchStepId();
	}, [data.step_name]);

	const toggleAnswerVisibility = useCallback((index: number) => {
		setVisibleAnswers((prevState) => {
			const newState = [...prevState];
			newState[index] = !newState[index];
			return newState;
		});
	}, []);

	const handleSendClick = async (inputValue: string) => {
		if (!inputValue) {
			setLoading(false);
			return;
		}
		try {
			setLoading(true);
			const faqRagResponse = await fetchFaqRagData(stepId!, inputValue, loggedInUserName);
			setRagData(faqRagResponse);
			setInputQuery(inputValue);
		} catch (error) {
			console.log('Error calling FAQ RAG API:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleLikeClick = async () => {
		try {
			const responseData = await updateFeedback(stepId!, inputQuery, 'like');
			setLikes(responseData.likes || 0);
		} catch (error) {
			console.error('Error updating feedback:', error);
		}
	};

	const handleDislikeClick = async () => {
		try {
			const responseData = await updateFeedback(stepId!, inputQuery, 'dislike');
			setDislikes(responseData.dislikes || 0);
		} catch (error) {
			console.error('Error updating feedback:', error);
		}
	};

	useEffect(() => {
		const fetchDataHistory = async () => {
			if (data.step_name === "Order submitted") {
				return;
			}
			if (data && !data.no_refresh_faq) {
				setLoading(true);
				try {
					const response = await fetchFaqDataHistory(data);
					setFaqDataHistory(response);
					setVisibleAnswers(new Array(response.q?.length || 0).fill(false));
				} catch (error) {
					console.log('Error calling FAQ data history API:', error);
				} finally {
					setLoading(false);
				}
			}
		};
		fetchDataHistory();
	}, [data]);

	const handleSearchResponseClose = () => {
		setRagData(null);
		setVisibleAnswers(new Array(faqDataHistory.q?.length || 0).fill(false)); // Reset visibleAnswers
	};

	return (
		<div className="p-1 bg-[#7EDEF1] rounded-b-xl min-h-44">
			<KnowledgeBaseSearch handleSendClick={handleSendClick} stepId={stepId!} />
			<div className="text-black leading-4 mt-4">
				<KnowledgeBaseResponse
					ragData={ragData}
					handleLikeClick={handleLikeClick}
					handleDislikeClick={handleDislikeClick}
					inputQuery={inputQuery}
					onSearchResponseClose={handleSearchResponseClose}
				/>
				{ragData === null && (
					<>
						{loading ? (
							<p className="text-[12px]">Loading...</p>
						) : (
							faqDataHistory.q &&
							faqDataHistory.q.map((question, index) => (
								<FAQItem
									key={index}
									question={question}
									answer={faqDataHistory.a && faqDataHistory.a[index] ? faqDataHistory.a[index] : ''}
									visible={visibleAnswers[index] || false}
									toggleVisibility={() => toggleAnswerVisibility(index)}
								/>
							))
						)}
					</>
				)}
			</div>
		</div>
	);
};

export default Insights;
